import AlertService from "../../../../services/AlertService";
import MCQExamService from "../../../../services/MCQExamService";
import StorageService from "../../../../services/StorageService";
import SharedService from "../../../../services/SharedService";
import AuthContext from "../../../../auth/AuthContext";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MCQExamQuestion from "./MCQQuestion";
import { useTimer } from "react-timer-hook";
import ChronometerIcon from "../../../../assets/icons/ChronoMeter.svg";
import QuestionMarkIcon from "../../../../assets/icons/QuestionMark.svg";
import PreviousIcon from "../../../../assets/icons/Previous.svg";
import WhitePreviousIcon from "../../../../assets/icons/WhitePreviousIcon.svg";
import NextIcon from "../../../../assets/icons/Next.svg";
import WhiteNextIcon from "../../../../assets/icons/WhiteNextIcon.svg";
import { Button, ProgressBar } from "react-bootstrap";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";
import { Skeleton } from "primereact/skeleton";
import { ThemeContext } from "../../../../theme/ThemeContext";

const MCQExam = () => {
  const navigate = useNavigate();
  const { assessmentName } = useParams();
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [assessmentExam, setAssessmentExam] = useState();
  const [questions, setQuestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState();
  const [selectedCardId, setSelectedCardId] = useState(0);
  const [selectedCard, setSelectedCard] = useState({});
  const [timerText, setTimerText] = useState("00:00:00");
  const [isExpired, setIsExpired] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [answeredCountPercent, setAnsweredCountPercent] = useState(0);
  const authContext = useContext(AuthContext);
  const scrollRef = useRef();
  const cookieName = `Assessment#${state?.id}#${authContext.userId}`;
  const [showDialog, setShowDialog] = useState(false);
  const submitControllerRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const { totalSeconds, seconds, minutes, hours, restart } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: async () => {
      if (!isExpired && assessmentExam) {
        setIsExpired(true);
        setTimerText("Expired");
        setShowDialog(true);
        await SharedService.Check(authContext);
      }
    },
  });

  const scrollToItem = () => {
    if (scrollRef.current) {
      const parentContainer = scrollRef.current.parentElement;
      const itemRect = scrollRef.current.getBoundingClientRect();
      const parentRect = parentContainer.getBoundingClientRect();
      const isVisible =
        itemRect.top >= parentRect.top &&
        itemRect.bottom <= parentRect.bottom &&
        itemRect.left >= parentRect.left &&
        itemRect.right <= parentRect.right;

      if (!isVisible) {
        scrollRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const assessmentAccess = authContext.getPermissions("assessments");
  if (assessmentAccess.filter((f) => f.access.includes(1)).length === 0) {
    navigate("/");
  }

const loadQuestions = useCallback(async (signal) => { 
    setLoading(true);
    setAnsweredCount(0);
    setAnsweredCountPercent(0);
    try {
      const data = await MCQExamService.GetAll(
        state?.id,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
              // if (data.data.isMCQExamCompleted) {
      //   navigate(`/assessments${assessmentName}/review-mcq`, {
      //     state: { id: state?.id },
      //   });
      // }
        let cQuestions = [];
        try {
          cQuestions = JSON.parse(StorageService.getLocal(cookieName));
        } catch {}
        if (cQuestions && cQuestions !== null && cQuestions.length > 0) {
          cQuestions.forEach((cq) => {
            let ques = data.data.questions.filter((f) => f.id === cq.id);
            if (ques.length > 0) {
              ques[0].isAnswered = cq.isAnswered;
              ques[0].isConfident = cq.isConfident;
              ques[0].options.forEach((opt) => {
                let opts = cq.options.filter((f) => f.id === opt.id);
                if (opts.length > 0) {
                  opt.isAnswer = opts[0].isAnswer;
                }
              });
            }
          });
        }
        setAssessmentExam(data.data);
        setQuestions(data.data.questions);

        if (data.data.questions.length > 0) {
          setSelectedCardId(data.data.questions[0].id);
          setSelectedCard(data.data.questions[0]);
          setSelectedIndex(0);
        }

        const time = new Date();
        time.setSeconds(time.getSeconds() + data.data.duration);
        restart(time);
        setIsExpired(data.data.duration === 0);
        if (data.data.duration === 0) {
          setTimerText("Expired");
        }
        setAnsweredCount(
          data.data.questions.filter((f) => f.isAnswered === true).length
        );
        setAnsweredCountPercent(
          (data.data.questions.filter((f) => f.isAnswered === true).length /
            data.data.totalMCQQuestions) * 100
        );

        if (data.data.startDateTime === null) {
          await MCQExamService.Start(state?.id, authContext);
        }
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [restart, authContext, cookieName, state?.id]);

  useEffect(() => {
    if (isExpired) {
      setTimerText("Expired");
    } else {
      setTimerText(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0"
        )}:${String(seconds).padStart(2, "0")}`
      );
    }
  }, [hours, minutes, seconds, isExpired]);

useEffect(() => {
    const controller = new AbortController(); 
    if (state?.id && !isExpired) {
      loadQuestions(controller.signal);
    }
    return () => controller.abort(); 
  }, [loadQuestions, state?.id, isExpired]);

  const changeHandler = (id, answerId, isConfident) => {
    setQuestions((prev) => {
      if (
        prev &&
        prev.length > 0 &&
        prev.filter((f) => f.id === id).length > 0
      ) {
        let question = prev.filter((f) => f.id === id);
        question = question[0];
        question.options = question.options.map((opt) => ({
          ...opt,
          isAnswer: answerId === opt.id,
        }));
        question.isAnswered = true;
        question.isConfident = isConfident;
      }
      return [...prev];
    });
    setTimeout(() => {
      setAnsweredCount(questions.filter((f) => f.isAnswered === true).length);
      setAnsweredCountPercent(
        (questions.filter((f) => f.isAnswered === true).length /
          assessmentExam.totalMCQQuestions) *
          100
      );
      StorageService.setLocal(cookieName, JSON.stringify(questions));
    }, 100);
  };

  const onCardClick = (data, i) => {
    let preId = selectedCardId;
    setSelectedCardId(data.id);
    setSelectedCard(data);
    setSelectedIndex(i);
    if (preId === undefined || preId === null || preId === 0) {
      setTimeout(() => {
        scrollToItem();
      }, 10);
    }
  };

  const moveToPrevious = (e) => {
    setSelectedIndex((prev) => {
      if (prev > 0) {
        return prev - 1;
      } else {
        return 0;
      }
    });
  };

  const moveToNext = (e) => {
    setSelectedIndex((prev) => {
      if (prev === questions.length - 1) {
        return prev;
      } else {
        return prev + 1;
      }
    });
  };

  useEffect(() => {
    if (questions && questions.length > 0) {
      setSelectedCardId(questions[selectedIndex].id);
      setSelectedCard(questions[selectedIndex]);
      setTimeout(() => {
        scrollToItem();
      }, 10);
    }
  }, [selectedIndex, questions]);

  useEffect(() => {
    localStorage.setItem("AssessmentExamTimer", totalSeconds);
  }, [totalSeconds]);

  const buildPayload = () => {
    let cQuestions = [];
    try {
      cQuestions = JSON.parse(StorageService.getLocal(cookieName));
      if (!cQuestions || cQuestions === null) {
        cQuestions = questions;
      }
    } catch {
      cQuestions = questions;
    }
    return {
      ...assessmentExam,
      questions: cQuestions,
      quizId: state?.id,
    };
  };



const onSubmitHandler = async () => {
  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payloadToSend = buildPayload();

    const data = await MCQExamService.Save(
      payloadToSend,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success("Assessment details saved successfully!");
      StorageService.deleteLocal(cookieName);
      await SharedService.Check(authContext);
      navigate(`/assessments/${assessmentName}`, {
        state: { id: state?.id },
      });
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    console.log(ex);
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);
  return (
    <Fragment>
      <div className="container-fluid question-container mt-3">
        <div className="row d-none d-md-flex">
          <div
            className="col-md-2 col-sm-4 scroll-no-buttons"
            style={{ maxHeight: "82vh", overflowY: "auto" }}
          >
            {loading
              ? Array.from({ length: 20 }).map((_, idx) => (
                  <div key={idx}>
                    <div className="mt-1 text-center">
                      <div className="pt-1 pb-1" title="Loading">
                        <Skeleton width="100%" height="1.5rem" />
                      </div>
                    </div>
                  </div>
                ))
              : questions &&
                questions.length > 0 &&
                questions.map((q, i) => (
                  <div key={i} ref={q.id === selectedCardId ? scrollRef : null}>
                    <div
                      className={`
                      mt-1 text-center cursor-pointer
                      ${
                        q.id === (selectedCardId ?? 0)
                          ? "quiz-active-card"
                          : q.isAnswered
                          ? q.isConfident
                            ? "quiz-answered-confident"
                            : "quiz-answered"
                          : "quiz-card"
                      }
                 `}
                      onClick={(e) => {
                        e.preventDefault();
                        onCardClick(q, i);
                      }}
                    >
                      <div
                        className="pt-1 pb-1 cursor-pointer"
                        title={q.question}
                      >
                        Question {i + 1}
                      </div>
                    </div>
                  </div>
                ))}
          </div>
          <div className="col-md-10 col-sm-8 view">
            {loading ? (
              <div className="d-flex bd-highlight">
                <div className="text-primary mt-2">
                  <Skeleton size="5rem" shape="circle" />
                </div>
                <div
                  className="p-2 mt-auto mb-auto"
                  style={{ color: "#104E66", width: "30%" }}
                >
                  <div className="title">
                    <Skeleton width="80%" height="1.5rem" />
                  </div>
                  <div className="title pt-1">
                    <Skeleton width="85%" height="1.5rem" />
                  </div>
                  <div className="title pt-1">
                    <Skeleton width="60%" height="1.5rem" />
                  </div>
                </div>

                <div className="ms-auto" style={{ width: "40%" }}>
                  <div
                    className="d-flex flex-column align-items-end"
                    style={{ width: "100%" }}
                  >
                    <div className="fw-light pt-1" title="Questions">
                      <Skeleton width="10rem" height="1.5rem" />
                    </div>
                    <div className="fw-light pt-1" title="Duration">
                      <Skeleton width="12rem" height="1.5rem" />
                    </div>
                    <div className="fw-light pt-1">
                      <Skeleton width="30rem" height="1.5rem" />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="d-flex bd-highlight flex-wrap flex-md-nowrap align-items-start mb-3 gap-3 quiz-header-responsive">
                <div className={isExpired ? "text-danger" : "text-primary"}>
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#FF9900"
                      strokeWidth="4"
                      strokeLinecap="round"
                      className="progress-circle"
                    />
                    <text
                      x="60"
                      y="67"
                      textAnchor="middle"
                      fontSize="18"
                      fontWeight="bold"
                      fill="#FF9900"
                    >
                      {timerText}
                    </text>
                  </svg>
                </div>
                <div
                  className="p-2 mt-auto mb-auto"
                  style={{ color: isDark ? "#ffffff" : "#104E66" }}
                >
                  <div className="title">
                    Assessment Name :{" "}
                    {assessmentExam && assessmentExam.assessmentName}
                  </div>
                  <div className="title pt-2">
                    Course : {assessmentExam && assessmentExam.courseName}
                  </div>
                  <div className="title pt-2">Type : MCQ</div>
                </div>

                <div className="ms-auto">
                  <div className="d-flex flex-column align-items-end">
                    <div className="fw-light pt-2" title="Questions">
                      <img
                        src={QuestionMarkIcon}
                        alt="Questions"
                        className="mb-1 me-1"
                      />{" "}
                      Questions :{" "}
                      {assessmentExam && assessmentExam.totalMCQQuestions}
                    </div>
                    <div className="fw-light pt-2" title="Duration">
                      <img
                        src={ChronometerIcon}
                        alt="Duration"
                        className="mb-1 me-1"
                      />{" "}
                      Duration :{" "}
                      {minutesToTime(assessmentExam && assessmentExam.duration)}
                    </div>
                    <div className="fw-light pt-2 w-100">
                      <div className="d-flex" style={{ width: "100%" }}>
                        <div>
                          {answeredCountPercent === 100
                            ? "Completed"
                            : "Progress"}
                        </div>
                        <div
                          className="ms-2 me-2 flex-grow-1"
                          style={{ minWidth: 301 }}
                        >
                          <ProgressBar
                            className="mt-2"
                            style={{ height: "10px", width: "301px" }}
                            variant={
                              answeredCountPercent < 25
                                ? "danger"
                                : answeredCountPercent < 50
                                ? "warning"
                                : answeredCountPercent < 75
                                ? "info"
                                : "success"
                            }
                            now={answeredCountPercent}
                          />
                        </div>
                        <div>
                          {answeredCount} /{" "}
                          {assessmentExam && assessmentExam.totalMCQQuestions}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {loading ? (
              <div>
                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                  <Skeleton width="100%" height="2px" />
                </div>
                <div className="d-flex ps-3 pe-3 mb-3">
                  <Skeleton width="60%" height="2rem" />
                </div>
                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                  <Skeleton width="100%" height="2px" />
                </div>
                {[40, 35, 30, 45, 40].map((w, i) => (
                  <div key={i} className="d-flex ps-3 pe-3 mb-3">
                    <center style={{ width: "5%" }} className="pe-1">
                      <Skeleton width="2rem" height="2rem" shape="circle" />
                    </center>
                    <div style={{ width: "100%" }} className="pe-1">
                      <Skeleton width={`${w}%`} height="2rem" />
                    </div>
                  </div>
                ))}

                <div className="d-flex justify-content-end mb-4 ps-3 pe-3">
                  <Skeleton width="15rem" height="2rem" />
                </div>
                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                  <Skeleton width="100%" height="2px" />
                </div>
                <div className="d-flex justify-content-between ps-3 pe-3 mb-3">
                  <Skeleton width="12rem" height="2rem" />
                  <Skeleton width="12rem" height="2rem" />
                </div>
              </div>
            ) : (
              questions &&
              questions.length > 0 && (
                <>
                  <MCQExamQuestion
                    isExpired={isExpired}
                    index={selectedIndex}
                    input={selectedCard}
                    changeHandler={changeHandler}
                  />
                  <div className="d-flex">
                    <div className="pt-3 ps-2">
                      {selectedIndex > 0 && (
                        <button
                          className="p-0 btn btn-outline"
                          onClick={moveToPrevious}
                        >
                          <div className="d-flex">
                            <img
                              src={isDark ? WhitePreviousIcon : PreviousIcon}
                              alt="Previous"
                              style={{ width: "40px", marginLeft: "-3px" }}
                            />
                            <div className="me-4 mt-2 ms-3">Previous</div>
                          </div>
                        </button>
                      )}
                    </div>

                    <div className="ms-auto pt-3 pe-2">
                      {selectedIndex < questions.length - 1 && (
                        <button
                          className="p-0 btn btn-outline"
                          onClick={moveToNext}
                        >
                          <div className="d-flex m-auto">
                            <div className="me-4 mt-2 ms-3">Next</div>
                            <img
                              src={isDark ? WhiteNextIcon : NextIcon}
                              style={{ width: "40px", marginRight: "-3px" }}
                              alt="Next"
                            />
                          </div>
                        </button>
                      )}

                      {selectedIndex === questions.length - 1 && (
                        <Button
                          variant="primary"
                          className="float-right mr-3 submit-button px-md-5 py-2"
                          onClick={() => setShowDialog(true)}
                        >
                          Submit
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-center align-items-center gap-5 mt-4">
                    <div className="d-flex align-items-center me-3">
                      <div
                        className="me-2"
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#7B97B7",
                        }}
                      ></div>
                      <span>Select</span>
                    </div>

                    <div className="d-flex align-items-center me-3">
                      <div
                        className="me-2"
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#62977D",
                        }}
                      ></div>
                      <span>Answered</span>
                    </div>

                    <div className="d-flex align-items-center">
                      <div
                        className="me-2"
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#3E5C48",
                        }}
                      ></div>
                      <span>Confident</span>
                    </div>
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </div>

      {isExpired ? (
        <ConfirmDialogBox
          visible={showDialog}
          onHide={() => {}} 
          onConfirm={() => {
            setShowDialog(false);
            onSubmitHandler();
          }}
          title={
            <>
              🕒 Your assessment time has expired. <br />
              Please click <strong>"Submit"</strong> to save your responses.
              <br />
              <small className="text-muted">
                Unsaved answers will not be recorded.
              </small>
            </>
          }
          confirmLabel="Submit"
          cancelLabel={null} 
          hideCloseIcon 
        />
      ) : (
        <ConfirmDialogBox
          visible={showDialog}
          onHide={() => setShowDialog(false)}
          onConfirm={() => {
            setShowDialog(false);
            onSubmitHandler();
          }}
          title={
            <>
              Are you sure you want to submit the <strong>assessment</strong>?{" "}
              <br />
              <small className="text-muted">
                Once submitted, you cannot make any changes.
              </small>
            </>
          }
          confirmLabel="Submit"
          cancelLabel="Cancel" 
        />
      )}
    </Fragment>
  );
};

export default MCQExam;
